import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Badge } from 'react-bootstrap';
import Layout from '../../components/layouts/Main';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import { PulseLoader } from "react-spinners";
import { formatDateTime } from '../../utils/dateHelpers';

const ProductStockDetails = () => {
    const { productId } = useParams();
    const [stockDetails, setStockDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const fetchStockDetails = useCallback(async () => {
        try {
            const response = await axios.get(`${config.API_BASE_URL}/stock/product/${productId}`);
            setStockDetails(response.data);
        } catch (error) {
            console.error('Error fetching stock details:', error);
        } finally {
            setIsLoading(false);
        }
    }, [productId]);

    useEffect(() => {
        fetchStockDetails();
    }, [fetchStockDetails]);

    if (isLoading) {
        return (
            <Layout>
                <div className="text-center py-5">
                    <PulseLoader color="#014b7cbb" size={9} />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
                    <li className="breadcrumb-item"><Link to="/stock">Stock Overview</Link></li>
                    <li className="breadcrumb-item active">{stockDetails.product.productName}</li>
                </ol>
            </nav>

            <Row className="mb-4">
                <Col>
                    <h2>{stockDetails.product.productName}</h2>
                    <p className="text-muted">
                        Group: {stockDetails.product.groupId.groupName} | 
                        SKU: {stockDetails.product.sku}
                    </p>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={3}>
                    <Card>
                        <Card.Body>
                            <h6>Total Stock</h6>
                            <h3>{stockDetails.totalStock} {stockDetails.product.unit}</h3>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card>
                        <Card.Body>
                            <h6>Reserved Stock</h6>
                            <h3>{stockDetails.reservedStock} {stockDetails.product.unit}</h3>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card>
                        <Card.Body>
                            <h6>Available Stock</h6>
                            <h3>{stockDetails.availableStock} {stockDetails.product.unit}</h3>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card>
                        <Card.Body>
                            <h6>Buffer Stock</h6>
                            <h3>{stockDetails.product.bufferStock} {stockDetails.product.unit}</h3>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">Stock Locations</h5>
                </Card.Header>
                <Card.Body>
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>Location</th>
                                    <th>Quantity</th>
                                    <th>Last Updated</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stockDetails.locations.map(location => (
                                    <tr key={location._id}>
                                        <td>{location.fullPath}</td>
                                        <td>{location.quantity} {stockDetails.product.unit}</td>
                                        <td>{formatDateTime(location.updatedAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card.Body>
            </Card>

            <Card>
                <Card.Header>
                    <h5 className="mb-0">Reserved Stock Details</h5>
                </Card.Header>
                <Card.Body>
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>SO Number</th>
                                    <th>Customer</th>
                                    <th>Quantity</th>
                                    <th>Status</th>
                                    <th>Expected Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stockDetails.reservations.map(reservation => (
                                    <tr key={reservation._id}>
                                        <td>
                                            <Link to={`/sales/orders/${reservation.orderId}`}>
                                                {reservation.orderNumber}
                                            </Link>
                                        </td>
                                        <td>{reservation.customerName}</td>
                                        <td>{reservation.quantity} {stockDetails.product.unit}</td>
                                        <td>
                                            <Badge bg={
                                                reservation.status === 'inProduction' ? 'warning' :
                                                reservation.status === 'inDispatch' ? 'success' :
                                                'info'
                                            }>
                                                {reservation.status}
                                            </Badge>
                                        </td>
                                        <td>{formatDateTime(reservation.expectedDate)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card.Body>
            </Card>
        </Layout>
    );
};

export default ProductStockDetails;
